import { useState, useMemo, useEffect, createContext, useContext, Fragment } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronRight, ChevronDown, AlertTriangle, GripVertical, Trash2, Plus, X, Save } from 'lucide-react';
import { formatCurrency, formatCurrencyShort } from '../lib/format.js';
import { useCategories } from '../hooks/useCategories.js';
import { useCashFlowLayout, useSaveCashFlowLayout } from '../hooks/useCashFlowLayout.js';
import { computeSubtotals } from '../lib/cashflow-subtotals.js';
import { buildEditableLayout, moveLayoutRow } from '../lib/cashflow-layout.js';
import SubtotalRow from './cashflow/SubtotalRow.jsx';
import CellPair from './cashflow/CellPair.jsx';
import MoveButtons from './cashflow/MoveButtons.jsx';
import TotalCells from './cashflow/TotalCells.jsx';
import { sumPeriodTotals } from '../lib/cashflow-totals.js';

const FLOW_BADGE = {
  income:  { label: 'Rec.',  className: 'bg-green-100 text-green-700' },
  expense: { label: 'Dép.',  className: 'bg-red-100 text-red-700' },
  neutral: { label: 'Neut.', className: 'bg-slate-100 text-slate-600' },
};

function isBudgetOverrun(actualCents, budgetCents, flowType) {
  if (actualCents == null || !budgetCents) return false;
  if (flowType === 'expense') {
    return Math.abs(actualCents) > Math.abs(budgetCents) * 1.05;
  }
  return actualCents < budgetCents * 0.95;
}

function formatXLabel(periodStart) {
  const date = new Date(periodStart + 'T00:00:00');
  return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
}

function addPerPeriod(acc, add) {
  const map = new Map(acc.map((p) => [p.period_start, { ...p }]));
  for (const p of add) {
    const prev = map.get(p.period_start) ?? {
      period_start:     p.period_start,
      actual_cents:     null,
      forecast_cents:   null,
      budget_cents:     null,
      actual_ht_cents:  null,
      forecast_ht_cents: null,
    };
    for (const k of ['actual_cents', 'forecast_cents', 'budget_cents', 'actual_ht_cents', 'forecast_ht_cents']) {
      if (p[k] != null) prev[k] = (prev[k] ?? 0) + p[k];
    }
    map.set(p.period_start, prev);
  }
  return [...map.values()];
}

function buildTree(categories, byCategoryMap, periodStarts) {
  const childrenByParent = new Map();
  for (const c of categories) {
    if (c.parent_id) {
      if (!childrenByParent.has(c.parent_id)) childrenByParent.set(c.parent_id, []);
      childrenByParent.get(c.parent_id).push(c);
    }
  }

  const emptyPerPeriod = () =>
    periodStarts.map((ps) => ({
      period_start:      ps,
      actual_cents:      null,
      forecast_cents:    null,
      budget_cents:      null,
      actual_ht_cents:   null,
      forecast_ht_cents: null,
    }));

  function buildNode(cat) {
    const rawChildren = childrenByParent.get(cat.id) ?? [];
    const children = rawChildren.map(buildNode);

    const own = byCategoryMap.get(cat.id);
    let per_period = own ? own.per_period : emptyPerPeriod();

    for (const child of children) {
      per_period = addPerPeriod(per_period, child.per_period);
    }

    const directHit = !!own;
    const childHit  = children.some((c) => c.hasActivity);

    return {
      id:         cat.id,
      name:       cat.name,
      parent_id:  cat.parent_id,
      flow_type:  own?.flow_type ?? cat.flow_type ?? 'expense',
      children:   children
        .filter((c) => c.hasActivity)
        .sort((a, b) => totalMagnitude(b) - totalMagnitude(a)),
      per_period,
      hasActivity: directHit || childHit,
    };
  }

  const roots = categories
    .filter((c) => !c.parent_id)
    .map(buildNode)
    .filter((r) => r.hasActivity)
    .sort((a, b) => totalMagnitude(b) - totalMagnitude(a));

  return roots;
}

function totalMagnitude(node) {
  return node.per_period.reduce((acc, p) => {
    const v = p.actual_cents ?? p.forecast_cents ?? p.budget_cents ?? 0;
    return acc + Math.abs(v);
  }, 0);
}

function collectDescendantIds(node, acc = []) {
  acc.push(node.id);
  for (const c of node.children ?? []) collectDescendantIds(c, acc);
  return acc;
}

function formatPeriodRange(periodStart, periodEnd) {
  const start = new Date(periodStart + 'T00:00:00');
  const end   = new Date(periodEnd + 'T00:00:00');
  const sameDay = start.toDateString() === end.toDateString();
  if (sameDay) {
    return start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  }
  return `${start.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} → ${end.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}

// Somme d'une série TVA (une valeur par colonne) ; null si tout est vide / nul.
function sumSeries(values) {
  const total = (values ?? []).reduce((acc, v) => acc + (v ?? 0), 0);
  return total !== 0 ? total : null;
}

// Génère un id temporaire pour les nouvelles subtotal rows avant sauvegarde
function tempId() {
  return `_temp_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * @param {function} [onCellClick]     — callback({ from, to, categoryIds, mode, title, subtitle, flowType }) => void
 * @param {boolean}  [editing]         — mode contrôlé optionnel (depuis le parent)
 * @param {function} [onEditingChange] — setter contrôlé optionnel (depuis le parent)
 * @param {object}   [cashFlowData]    — réponse complète de GET /v1/cash-flow (pour vat_summary)
 */
export default function ActualVsBudgetTable({
  byCategory = [],
  buckets = [],
  onCellClick,
  onVatCellClick,
  editing: controlledEditing,
  onEditingChange,
  cashFlowData,
}) {
  const { data: categoriesRaw } = useCategories();
  const categories = Array.isArray(categoriesRaw)
    ? categoriesRaw
    : categoriesRaw?.categories ?? categoriesRaw?.data ?? [];

  const { data: savedLayout = [], isLoading: layoutLoading } = useCashFlowLayout();
  const saveMutation = useSaveCashFlowLayout();

  // Mode édition — contrôlé depuis le parent si les props editing/onEditingChange sont fournies
  const [internalEditing, setInternalEditing] = useState(false);
  const editing    = controlledEditing  ?? internalEditing;
  const setEditing = onEditingChange    ?? setInternalEditing;

  // Layout local (en cours d'édition avant save)
  const [localLayout, setLocalLayout] = useState(null);

  const [expandedL1, setExpandedL1] = useState(new Set());
  const [expandedL2, setExpandedL2] = useState(new Set());

  // Quand le parent active le mode édition directement (controlledEditing: true),
  // initialise localLayout si ce n'est pas déjà fait.
  useEffect(() => {
    if (editing && !localLayout) {
      setLocalLayout(buildEditableLayout(savedLayout, tree));
    }
    if (!editing && localLayout) {
      setLocalLayout(null);
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayISO = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  // handleCellClick : peu importe la cellule cliquée (Prévi ou Réel), on ouvre
  // le panel en mode='both' pour permettre la comparaison côte à côte des deux
  // jeux de données. Le paramètre _mode est ignoré (gardé pour compatibilité).
  function handleCellClick(node, col, _mode) {
    if (!onCellClick) return;
    onCellClick({
      from:        col.period_start,
      to:          col.period_end,
      categoryIds: collectDescendantIds(node),
      mode:        'both',
      title:       node.name,
      subtitle:    formatPeriodRange(col.period_start, col.period_end),
      flowType:    node.flow_type ?? 'expense',
    });
  }

  const periodStarts = useMemo(() => buckets.map((b) => b.period_start), [buckets]);

  // Map period_start → bucket pour les 2 lignes fixes (unclassified + balance_end)
  const bucketByPeriod = useMemo(() => {
    const m = new Map();
    for (const b of buckets) m.set(b.period_start, b);
    return m;
  }, [buckets]);

  const cols = useMemo(
    () =>
      buckets.map((b) => ({
        period_start: b.period_start,
        period_end:   b.period_end,
        is_future:    b.is_future,
        // Non-null quand le prévisionnel du mois est figé (snapshot) → cadenas
        frozen_at:    b.frozen_at ?? null,
        // Label compact pour mobile : "avr. 26"
        shortLabel:   formatXLabel(b.period_start),
      })),
    [buckets]
  );

  const byCategoryMap = useMemo(() => {
    const m = new Map();
    for (const c of byCategory) m.set(c.category_id, c);
    return m;
  }, [byCategory]);

  // Arbre complet (fallback + résolution des nœuds par category_id)
  const tree = useMemo(() => {
    if (!categories.length) return [];
    return buildTree(categories, byCategoryMap, periodStarts);
  }, [categories, byCategoryMap, periodStarts]);

  // nodeById : map category_id → treeNode pour la résolution layout
  const nodeById = useMemo(() => {
    const m = new Map();
    function walk(node) {
      m.set(node.id, node);
      for (const c of node.children ?? []) walk(c);
    }
    for (const r of tree) walk(r);
    return m;
  }, [tree]);

  // Layout actif : savedLayout ou null (fallback arbre)
  // En édition on utilise localLayout
  const activeLayout = editing ? localLayout : savedLayout;
  const hasLayout = activeLayout && activeLayout.length > 0;

  // Calcul subtotaux : on utilise nodeById qui a les per_period agrégés
  // (root + enfants), pas byCategoryMap qui n'a que les tx directes par
  // category_id. Sans ça, une racine type "Chiffre d'affaires" sans tx
  // directe (toutes en sous-cat Qonto) renvoie 0 dans le subtotal.
  const subtotalData = useMemo(() => {
    if (!hasLayout) return new Map();
    return computeSubtotals(activeLayout, nodeById, periodStarts);
  }, [activeLayout, nodeById, periodStarts, hasLayout]);

  // Catégories non placées (présentes dans tree mais absentes du layout)
  const unplacedNodes = useMemo(() => {
    if (!hasLayout) return [];
    const placedIds = new Set(
      activeLayout
        .filter((r) => r.kind === 'category')
        .map((r) => r.category_id)
    );
    return tree.filter((r) => !placedIds.has(r.id));
  }, [hasLayout, activeLayout, tree]);

  // --- Mode édition ---

  function enterEditing() {
    // Layout sauvegardé + catégories non encore placées (ajoutées à la fin)
    // pour que chaque ligne soit déplaçable.
    setLocalLayout(buildEditableLayout(savedLayout, tree));
    setEditing(true);
  }

  function cancelEditing() {
    setLocalLayout(null);
    setEditing(false);
  }

  async function saveEditing() {
    if (!localLayout) return;
    const rows = localLayout.map((r, i) => ({
      kind:        r.kind,
      category_id: r.kind === 'category' ? r.category_id : null,
      label:       r.kind === 'subtotal' ? (r.label || 'Solde intermédiaire') : null,
      position:    i,
      // Transmet l'id uniquement si c'est un id serveur (pas un _temp_)
      ...(r.id && !r.id.startsWith('_temp_') ? { id: r.id } : {}),
    }));
    await saveMutation.mutateAsync(rows);
    setLocalLayout(null);
    setEditing(false);
  }

  function addSubtotal() {
    setLocalLayout((prev) => [
      ...(prev ?? []),
      {
        id:          tempId(),
        kind:        'subtotal',
        category_id: null,
        label:       'Nouveau solde',
        position:    (prev?.length ?? 0),
      },
    ]);
  }

  function removeRow(rowId) {
    setLocalLayout((prev) => prev.filter((r) => r.id !== rowId));
  }

  function moveRow(rowId, delta) {
    setLocalLayout((prev) => moveLayoutRow(prev, rowId, delta));
  }

  function updateSubtotalLabel(rowId, newLabel) {
    setLocalLayout((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, label: newLabel } : r))
    );
  }

  // Drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 150, tolerance: 10 } })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setLocalLayout((prev) => {
      const oldIndex = prev.findIndex((r) => r.id === active.id);
      const newIndex = prev.findIndex((r) => r.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  // Modifications en cours (pour la barre sticky)
  const modCount = useMemo(() => {
    if (!editing || !localLayout) return 0;
    return localLayout.filter((r) => r.id?.startsWith('_temp_')).length +
      localLayout.filter((r) => r.kind === 'subtotal' && !r.id?.startsWith('_temp_')).length;
    // Comptage simplifié : on indique juste le nb de rows (pas un vrai diff)
  }, [editing, localLayout]);

  // --- Rendu ---

  if (!byCategory.length || !buckets.length) {
    return (
      <div className="text-sm text-slate-400 py-6 text-center">
        Aucune donnée par catégorie
      </div>
    );
  }

  function toggleL1(id) {
    setExpandedL1((prev) => {
      const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
  }
  function toggleL2(id) {
    setExpandedL2((prev) => {
      const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
  }

  // Rendu d'une ligne catégorie (utilisé dans les deux modes)
  function renderCategoryRow(root, opts = {}) {
    const { wrapDnd = false, move } = opts;
    const isOpenL1 = expandedL1.has(root.id);
    const hasChildren = root.children.length > 0;

    const row = (
      <FragmentRow
        key={root.id}
        node={root}
        level={0}
        cols={cols}
        expandable={hasChildren}
        isOpen={isOpenL1}
        onToggle={() => toggleL1(root.id)}
        onCellClick={onCellClick ? (col, mode) => handleCellClick(root, col, mode) : undefined}
        editing={editing}
        onRemove={editing ? () => removeRow(root.id) : undefined}
        move={move}
      >
        {isOpenL1 &&
          root.children.map((child) => {
            const isOpenL2 = expandedL2.has(child.id);
            const hasGrand = child.children.length > 0;
            return (
              <FragmentRow
                key={child.id}
                node={child}
                level={1}
                cols={cols}
                expandable={hasGrand}
                isOpen={isOpenL2}
                onToggle={() => toggleL2(child.id)}
                onCellClick={onCellClick ? (col, mode) => handleCellClick(child, col, mode) : undefined}
              >
                {isOpenL2 &&
                  child.children.map((g) => (
                    <FragmentRow
                      key={g.id}
                      node={g}
                      level={2}
                      cols={cols}
                      expandable={false}
                      onCellClick={onCellClick ? (col, mode) => handleCellClick(g, col, mode) : undefined}
                    />
                  ))}
              </FragmentRow>
            );
          })}
      </FragmentRow>
    );

    if (!wrapDnd) return row;
    return (
      <SortableTrGroup key={root.id} id={root.id}>
        {row}
      </SortableTrGroup>
    );
  }

  // Rendu des rows du layout (mode layout actif)
  function renderLayoutRows(layout, draggable = false) {
    return layout.map((row, idx) => {
      const move = draggable
        ? { onMove: (delta) => moveRow(row.id, delta), isFirst: idx === 0, isLast: idx === layout.length - 1 }
        : undefined;

      if (row.kind === 'category') {
        const node = nodeById.get(row.category_id);
        if (!node) return null;

        const content = renderCategoryRow(node, { wrapDnd: false, move });
        if (!draggable) return content;
        return (
          <SortableTrGroup key={row.id} id={row.id}>
            {content}
          </SortableTrGroup>
        );
      }

      if (row.kind === 'subtotal') {
        const perPeriod = subtotalData.get(row.id) ?? [];
        const subtotalEl = (
          <SubtotalRow
            key={row.id}
            subtotal={row}
            perPeriod={perPeriod}
            cols={cols}
            todayISO={todayISO}
            editing={editing}
            onLabelChange={editing ? updateSubtotalLabel : undefined}
            onRemove={editing ? removeRow : undefined}
            move={move}
          />
        );
        if (!draggable) return subtotalEl;
        return (
          <SortableTrGroup key={row.id} id={row.id}>
            {subtotalEl}
          </SortableTrGroup>
        );
      }

      return null;
    });
  }

  const layoutIds = activeLayout?.map((r) => r.id) ?? [];

  // Chaque bucket = 2 sous-colonnes ; la largeur minimale du tableau augmente.
  // 220 (label sticky) + nb_mois × 2 × 60 px minimum.
  // 220 (label sticky) + nb_mois × 2 × 62 + la paire Total (2 × 96).
  const tableMinWidth = 220 + cols.length * 2 * 62 + 2 * 96;

  const tableContent = (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <span className="text-xs font-semibold text-slate-600">Détail par catégorie</span>
        {editing && <span className="text-xs text-blue-600 font-medium">Mode édition</span>}
      </div>

      <table
        className="text-xs border-separate w-full"
        style={{ minWidth: `${tableMinWidth}px`, borderSpacing: 0 }}
      >
        {/* ── Header sur 2 lignes ── */}
        <thead>
          {/* Ligne 1 : nom des mois (colSpan=2 chacun) */}
          <tr className="border-b border-slate-100">
            <th
              rowSpan={2}
              className="sticky left-0 bg-white z-10 text-left px-3 py-2 text-xs font-semibold text-slate-600 border-r border-slate-100 min-w-[220px] border-b border-slate-200"
            >
              {editing && <span className="inline-block w-6" />}
              Catégorie
            </th>
            {cols.map((col) => (
              <th
                key={col.period_start}
                colSpan={2}
                className={[
                  'px-1 py-1.5 text-center text-[11px] font-semibold text-slate-600 border-r border-slate-200 last:border-r-0',
                  col.is_future ? 'bg-slate-50/60' : '',
                ].join(' ')}
              >
                {col.shortLabel}
              </th>
            ))}
            <th
              colSpan={2}
              className="px-1 py-1.5 text-center text-[11px] font-semibold text-slate-700 bg-slate-50 border-l-2 border-slate-300"
              title="Total sur la période affichée"
            >
              Total
            </th>
          </tr>
          {/* Ligne 2 : sous-headers Réel | Prévi pour chaque mois */}
          <tr className="border-b border-slate-200">
            {cols.map((col) => (
              <Fragment key={col.period_start}>
                <th
                  className={[
                    'min-w-[60px] px-1.5 py-1 text-[10px] text-slate-400 font-medium text-right border-r border-slate-100',
                    col.is_future ? 'bg-slate-50/30' : '',
                  ].join(' ')}
                >
                  Réel
                </th>
                <th
                  className={[
                    'min-w-[60px] px-1.5 py-1 text-[10px] text-slate-400 font-medium text-right italic border-r border-slate-200 last:border-r-0',
                    col.is_future ? 'bg-slate-50/40' : '',
                  ].join(' ')}
                >
                  Prévi
                </th>
              </Fragment>
            ))}
            <th className="min-w-[96px] px-2 py-1 text-[10px] text-slate-500 font-medium text-right whitespace-nowrap bg-slate-50 border-l-2 border-slate-300 border-r border-slate-100">
              Réel
            </th>
            <th className="min-w-[96px] px-2 py-1 text-[10px] text-slate-500 font-medium text-right italic whitespace-nowrap bg-slate-50">
              Prévi
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100">
          {hasLayout ? (
            <>
              {editing ? (
                <SortableContext items={layoutIds} strategy={verticalListSortingStrategy}>
                  {renderLayoutRows(activeLayout, true)}
                </SortableContext>
              ) : (
                renderLayoutRows(activeLayout, false)
              )}

              {/* Catégories non placées */}
              {unplacedNodes.length > 0 && (
                <>
                  <tr>
                    {/* +1 pour la colonne label, × 2 pour les paires */}
                    <td
                      colSpan={cols.length * 2 + 1}
                      className="px-3 py-1.5 text-[10px] text-slate-400 font-medium bg-slate-50 border-y border-dashed border-slate-200"
                    >
                      Catégories non placées
                    </td>
                  </tr>
                  {unplacedNodes.map((node) => renderCategoryRow(node))}
                </>
              )}
            </>
          ) : (
            // Fallback : ordre par magnitude (comportement original)
            tree.map((root) => {
              const isOpenL1 = expandedL1.has(root.id);
              const hasChildren = root.children.length > 0;
              return (
                <FragmentRow
                  key={root.id}
                  node={root}
                  level={0}
                  cols={cols}
                  expandable={hasChildren}
                  isOpen={isOpenL1}
                  onToggle={() => toggleL1(root.id)}
                  onCellClick={onCellClick ? (col, mode) => handleCellClick(root, col, mode) : undefined}
                >
                  {isOpenL1 &&
                    root.children.map((child) => {
                      const isOpenL2 = expandedL2.has(child.id);
                      const hasGrand = child.children.length > 0;
                      return (
                        <FragmentRow
                          key={child.id}
                          node={child}
                          level={1}
                          cols={cols}
                          expandable={hasGrand}
                          isOpen={isOpenL2}
                          onToggle={() => toggleL2(child.id)}
                          onCellClick={onCellClick ? (col, mode) => handleCellClick(child, col, mode) : undefined}
                        >
                          {isOpenL2 &&
                            child.children.map((g) => (
                              <FragmentRow
                                key={g.id}
                                node={g}
                                level={2}
                                cols={cols}
                                expandable={false}
                                onCellClick={onCellClick ? (col, mode) => handleCellClick(g, col, mode) : undefined}
                              />
                            ))}
                        </FragmentRow>
                      );
                    })}
                </FragmentRow>
              );
            })
          )}

          {/* ── Ligne fixe 1 : Transactions à catégoriser ── */}
          <tr className="bg-amber-50 border-t border-slate-300">
            <td className="sticky left-0 bg-amber-50 z-10 px-3 py-1.5 border-r border-slate-200 text-xs italic text-slate-600 min-w-[220px]">
              Transactions à catégoriser
            </td>
            {cols.map((col) => {
              const bucket = bucketByPeriod.get(col.period_start);
              const cents = bucket?.unclassified_actual_cents ?? null;
              const hasValue = cents != null && cents !== 0;
              const isClickable = hasValue && !col.is_future && !!onCellClick;
              return (
                <Fragment key={col.period_start}>
                  {/* Réel */}
                  <td
                    className={[
                      'px-1.5 py-1.5 text-right tabular-nums text-xs border-r border-slate-100',
                      hasValue ? 'text-orange-600' : 'text-slate-300',
                      isClickable ? 'cursor-pointer hover:bg-amber-100 transition-colors' : '',
                    ].join(' ')}
                    title={isClickable ? 'Cliquer pour recatégoriser' : undefined}
                    onClick={
                      isClickable
                        ? () =>
                            onCellClick({
                              from:          col.period_start,
                              to:            col.period_end,
                              unclassified:  true,
                              mode:          'actuals',
                              title:         'Transactions à catégoriser',
                              subtitle:      formatPeriodRange(col.period_start, col.period_end),
                            })
                        : undefined
                    }
                  >
                    {hasValue ? formatCurrencyShort(cents) : '—'}
                  </td>
                  {/* Prévi — toujours vide */}
                  <td className="px-1.5 py-1.5 text-right text-xs text-slate-300 border-r border-slate-200 last:border-r-0 italic">
                    —
                  </td>
                </Fragment>
              );
            })}
            <TotalCells
              tone="plain"
              colorClass="text-orange-600"
              actual={sumPeriodTotals(
                cols.map((col) => ({ period_start: col.period_start, actual_cents: bucketByPeriod.get(col.period_start)?.unclassified_actual_cents ?? null })),
                cols
              ).actual || null}
              forecast={null}
            />
          </tr>

          {/* ── Lignes TVA : collectée (positive) + déductible (négative) ── */}
          {cashFlowData?.vat_summary && (() => {
            const vs = cashFlowData.vat_summary;
            // Nouveau contrat API : 4 arrays séparés Réel/Prévi.
            // Fallback gracieux vers l'ancien contrat (collected_per_period) pendant la fenêtre de déploiement.
            const hasNewContract =
              Array.isArray(vs.collected_actual_per_period) ||
              Array.isArray(vs.collected_forecast_per_period);

            const collectedActual   = vs.collected_actual_per_period   ?? (hasNewContract ? [] : vs.collected_per_period   ?? []);
            const collectedForecast = vs.collected_forecast_per_period ?? (hasNewContract ? [] : []);
            const deductibleActual  = vs.deductible_actual_per_period  ?? (hasNewContract ? [] : vs.deductible_per_period  ?? []);
            const deductibleForecast = vs.deductible_forecast_per_period ?? (hasNewContract ? [] : []);

            const hasCollected  = collectedActual.some((v) => v !== 0 && v != null)
                                || collectedForecast.some((v) => v !== 0 && v != null);
            const hasDeductible = deductibleActual.some((v) => v !== 0 && v != null)
                                || deductibleForecast.some((v) => v !== 0 && v != null);

            if (!hasCollected && !hasDeductible) return null;
            return (
              <>
                {hasCollected && (
                  <tr className="bg-green-50/40 border-t border-slate-200">
                    <td className="sticky left-0 bg-green-50/40 z-10 px-3 py-1.5 border-r border-slate-200 text-xs italic text-green-700 min-w-[220px]">
                      TVA collectée
                    </td>
                    {cols.map((col, i) => {
                      const actual   = collectedActual[i]   ?? 0;
                      const forecast = collectedForecast[i] ?? 0;
                      const actualClickable   = actual !== 0   && !!onVatCellClick;
                      const forecastClickable = forecast !== 0 && !!onVatCellClick;
                      return (
                        <Fragment key={col.period_start}>
                          <td
                            className={`px-1.5 py-1.5 text-right tabular-nums text-xs text-green-600 border-r border-slate-100 font-medium ${actualClickable ? 'cursor-pointer hover:bg-green-100/70' : ''}`}
                            onClick={actualClickable ? () => onVatCellClick(col, 'collected', 'actuals') : undefined}
                            title={actualClickable ? 'Voir le détail TVA collectée (réel)' : undefined}
                          >
                            {actual !== 0 ? formatCurrencyShort(actual) : '—'}
                          </td>
                          <td
                            className={`px-1.5 py-1.5 text-right tabular-nums text-xs text-green-600/60 border-r border-slate-200 last:border-r-0 italic ${forecastClickable ? 'cursor-pointer hover:bg-green-100/70' : ''}`}
                            onClick={forecastClickable ? () => onVatCellClick(col, 'collected', 'forecasts') : undefined}
                            title={forecastClickable ? 'Voir le détail TVA collectée (prévi)' : undefined}
                          >
                            {forecast !== 0 ? formatCurrencyShort(forecast) : '—'}
                          </td>
                        </Fragment>
                      );
                    })}
                    <TotalCells
                      tone="plain"
                      colorClass="text-green-600"
                      actual={sumSeries(collectedActual)}
                      forecast={sumSeries(collectedForecast)}
                    />
                  </tr>
                )}
                {hasDeductible && (
                  <tr className="bg-red-50/20 border-t border-slate-100">
                    <td className="sticky left-0 bg-red-50/20 z-10 px-3 py-1.5 border-r border-slate-200 text-xs italic text-red-600 min-w-[220px]">
                      TVA déductible
                    </td>
                    {cols.map((col, i) => {
                      const actual   = deductibleActual[i]   ?? 0;
                      const forecast = deductibleForecast[i] ?? 0;
                      const actualClickable   = actual !== 0   && !!onVatCellClick;
                      const forecastClickable = forecast !== 0 && !!onVatCellClick;
                      return (
                        <Fragment key={col.period_start}>
                          <td
                            className={`px-1.5 py-1.5 text-right tabular-nums text-xs text-red-600 border-r border-slate-100 font-medium ${actualClickable ? 'cursor-pointer hover:bg-red-100/70' : ''}`}
                            onClick={actualClickable ? () => onVatCellClick(col, 'deductible', 'actuals') : undefined}
                            title={actualClickable ? 'Voir le détail TVA déductible (réel)' : undefined}
                          >
                            {actual !== 0 ? formatCurrencyShort(actual) : '—'}
                          </td>
                          <td
                            className={`px-1.5 py-1.5 text-right tabular-nums text-xs text-red-600/60 border-r border-slate-200 last:border-r-0 italic ${forecastClickable ? 'cursor-pointer hover:bg-red-100/70' : ''}`}
                            onClick={forecastClickable ? () => onVatCellClick(col, 'deductible', 'forecasts') : undefined}
                            title={forecastClickable ? 'Voir le détail TVA déductible (prévi)' : undefined}
                          >
                            {forecast !== 0 ? formatCurrencyShort(forecast) : '—'}
                          </td>
                        </Fragment>
                      );
                    })}
                    <TotalCells
                      tone="plain"
                      colorClass="text-red-600"
                      actual={sumSeries(deductibleActual)}
                      forecast={sumSeries(deductibleForecast)}
                    />
                  </tr>
                )}
              </>
            );
          })()}

          {/* ── Ligne fixe 2 : Solde fin de période (montant complet pour rapprochement bancaire) ── */}
          <tr className="bg-slate-100 border-t border-slate-300 font-semibold">
            <td className="sticky left-0 bg-slate-100 z-10 px-3 py-1.5 border-r border-slate-200 text-xs font-semibold text-slate-700 min-w-[220px]">
              Solde fin de période
            </td>
            {cols.map((col) => {
              const bucket = bucketByPeriod.get(col.period_start);
              const cents = bucket?.balance_end_cents ?? null;
              return (
                <td
                  key={col.period_start}
                  colSpan={2}
                  className="min-w-[140px] px-1.5 py-1.5 text-right tabular-nums text-xs font-semibold text-slate-800 border-r border-slate-200 last:border-r-0 whitespace-nowrap"
                  title={`Solde cumulé au ${col.period_end}`}
                >
                  {cents != null ? formatCurrency(cents) : '—'}
                </td>
              );
            })}
            <td colSpan={2} className="bg-slate-50 border-l-2 border-slate-300 text-right px-1.5 text-xs text-slate-300">
              —
            </td>
          </tr>
        </tbody>
      </table>

      {/* Bouton "Ajouter un solde intermédiaire" en mode édition */}
      {editing && (
        <div className="px-3 py-2 border-t border-slate-100">
          <button
            type="button"
            onClick={addSubtotal}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1.5 rounded transition-colors"
          >
            <Plus size={13} />
            Ajouter un solde intermédiaire
          </button>
        </div>
      )}
    </div>
  );

  // Sticky bar d'édition (mobile-first : fixée en bas, lisible sur 375px)
  const editingBar = editing && (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-blue-200 shadow-lg px-4 py-3 flex items-center gap-3 sm:gap-4">
      <span className="text-xs text-slate-500 flex-1">
        {localLayout?.length ?? 0} ligne{(localLayout?.length ?? 0) > 1 ? 's' : ''} dans le layout
      </span>
      <button
        type="button"
        onClick={cancelEditing}
        className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-800 px-3 py-2 rounded border border-slate-200 hover:bg-slate-50 transition-colors min-h-[44px]"
      >
        <X size={13} />
        Annuler
      </button>
      <button
        type="button"
        onClick={saveEditing}
        disabled={saveMutation.isPending}
        className="flex items-center gap-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-3 py-2 rounded transition-colors min-h-[44px] font-medium"
      >
        <Save size={13} />
        {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </div>
  );

  if (editing) {
    return (
      <>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {tableContent}
        </DndContext>
        {editingBar}
        {/* Espace pour que le contenu ne soit pas masqué par la barre sticky */}
        <div className="h-20" />
      </>
    );
  }

  return tableContent;
}

// ---------------------------------------------------------------------------
// SortableTrGroup : wrap un groupe de <tr> (row L0 + sous-rows) dans le contexte
// dnd-kit. dnd-kit requiert un nœud DOM unique comme ref. On utilise un Fragment
// avec le premier <tr> portant la ref et les attributs drag.
// ---------------------------------------------------------------------------
function SortableTrGroup({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : undefined,
  };

  // On clone le premier enfant React pour y injecter ref + style + attributs drag
  // children est ici un FragmentRow ou SubtotalRow (qui retourne <>...</>)
  // On enroule dans un wrapper invisible via CSS display:contents pour conserver le layout table
  return (
    <DragGroupContext.Provider value={{ listeners, setNodeRef, style, attributes, isDragging }}>
      {children}
    </DragGroupContext.Provider>
  );
}

export const DragGroupContext = createContext(null);

// ---------------------------------------------------------------------------
// FragmentRow : ligne L0/L1/L2 de catégorie
// ---------------------------------------------------------------------------
function FragmentRow({
  node, level, cols, expandable, isOpen, onToggle, onCellClick, children,
  editing = false, onRemove, move,
}) {
  const dragCtx = useContext(DragGroupContext);
  const badge = FLOW_BADGE[node.flow_type] ?? FLOW_BADGE.neutral;

  const rowBg =
    level === 0
      ? 'bg-white hover:bg-slate-50 font-medium text-slate-800'
      : level === 1
      ? 'bg-slate-50 hover:bg-slate-100 text-slate-700'
      : 'bg-white hover:bg-slate-50 text-slate-600';

  const indent = level * 18;

  // La ref/style dnd-kit s'applique uniquement sur la row L0 (la poignée)
  const trProps =
    level === 0 && dragCtx
      ? {
          ref:   dragCtx.setNodeRef,
          style: dragCtx.style,
          ...dragCtx.attributes,
        }
      : {};

  return (
    <>
      <tr className={rowBg} {...trProps}>
        {/* Colonne label sticky */}
        <td className={`sticky left-0 ${rowBg} z-10 px-3 py-1.5 border-r border-slate-100`}>
          <div className="flex items-center gap-1.5" style={{ paddingLeft: `${indent}px` }}>
            {/* Handle drag visible uniquement en édition sur le niveau L0 */}
            {editing && level === 0 && dragCtx && (
              <button
                type="button"
                {...dragCtx.listeners}
                aria-label="Déplacer cette ligne"
                className="shrink-0 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 touch-none p-0.5"
                style={{ minWidth: '24px', minHeight: '44px', display: 'flex', alignItems: 'center' }}
              >
                <GripVertical size={14} />
              </button>
            )}
            {editing && level === 0 && move && (
              <MoveButtons onMove={move.onMove} isFirst={move.isFirst} isLast={move.isLast} />
            )}
            {expandable ? (
              <button
                type="button"
                onClick={onToggle}
                aria-label={isOpen ? 'Replier' : 'Déplier'}
                className="shrink-0 text-slate-400 hover:text-slate-700"
              >
                {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
            ) : (
              <span className="shrink-0 w-3" />
            )}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${badge.className}`}>
              {badge.label}
            </span>
            <span className="truncate" title={node.name}>
              {node.name}
            </span>
            {/* Bouton suppression du layout (uniquement en édition L0) */}
            {editing && level === 0 && onRemove && (
              <button
                type="button"
                onClick={() => onRemove(node.id)}
                aria-label="Retirer du layout"
                className="ml-auto shrink-0 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </td>

        {/* 2 cellules par bucket : Réel | Prévi via CellPair — affichage en HT si disponible */}
        {cols.map((col) => {
          const pp = node.per_period.find((p) => p.period_start === col.period_start);
          // Affichage HT avec fallback sur TTC si HT non disponible
          const actualCents   = pp?.actual_ht_cents   ?? pp?.actual_cents   ?? null;
          const forecastCents = pp?.forecast_ht_cents ?? pp?.forecast_cents ?? null;
          const budgetCents   = pp?.budget_cents ?? null;

          // Le dépassement budget reste calculé sur TTC (montant de référence fiable)
          const overrun = !col.is_future && isBudgetOverrun(pp?.actual_cents ?? null, budgetCents, node.flow_type);

          return (
            <CellPair
              key={col.period_start}
              forecastCents={forecastCents}
              actualCents={actualCents}
              isFuture={col.is_future}
              flowType={node.flow_type}
              isClickable={!!onCellClick}
              onClickForecast={onCellClick ? () => onCellClick(col, 'forecasts') : undefined}
              onClickActual={onCellClick ? () => onCellClick(col, 'actuals') : undefined}
              isOverrun={overrun}
              frozenAt={col.frozen_at}
            />
          );
        })}
        {(() => {
          const t = sumPeriodTotals(node.per_period, cols);
          return <TotalCells actual={t.actual} forecast={t.forecast} tone="category" flowType={node.flow_type} />;
        })()}
      </tr>
      {children}
    </>
  );
}
